import RecursiveMenu from '@app/layouts/Sidebar/Components/RecursiveMenu/RecursiveMenu';
import React, { memo, Suspense, useCallback, useMemo } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { MenuIcon } from './MenuIcon';
import { ExpandedMenuItemProps } from './types';
import { getLeftClass, getPaddingLeftClass } from './utils';

/**
 * ExpandedMenuItem component for rendering menu items when sidebar is expanded
 * Shows full labels, icons, and handles nested menu structures
 * Optimized with React.memo and useCallback to prevent unnecessary re-renders
 */
const ExpandedMenuItemComponent: React.FC<ExpandedMenuItemProps> = ({
  item,
  isActive,
  hasChildren,
  hasActiveChild,
  isOpen,
  depth,
  index,
  isSidebarCollapsed,
  onToggle,
}) => {
  /**
   * Memoized click handler to prevent unnecessary re-renders
   * Toggles submenu for items with children
   */
  const handleClick = useCallback(() => {
    if (hasChildren && !isSidebarCollapsed) {
      onToggle(item.key);
    }
  }, [hasChildren, isSidebarCollapsed, onToggle, item.key]);

  // Memoized CSS classes to avoid recalculation on every render
  const containerClasses = useMemo(
    () => `
    relative flex items-center w-full gap-x-2 cursor-pointer 
    rounded-md transition-all duration-200 text-sm text-white/90
    ${isActive ? '' : 'hover:text-white hover:bg-app-gray-light'}
    ${hasChildren ? 'pr-6' : ''}
  `,
    [isActive, hasChildren]
  );

  const labelClasses = useMemo(
    () => `
    relative inline-block z-10 w-full whitespace-nowrap 
    px-5 py-3 rounded-4xl text-white/70 hover:text-white/90
    ${isActive ? '!text-app-primary-600' : 'text-app-text-light'}
  `,
    [isActive]
  );

  const wrapperClasses = useMemo(
    () => `
    w-full justify-center 
    ${index === 0 && depth !== 0 ? 'mt-0' : ''}
  `,
    [index, depth]
  );

  const paddingClasses = useMemo(
    () => `
    w-full mx-auto relative flex items-center justify-center 
    ${isSidebarCollapsed ? 'px-0' : getPaddingLeftClass(depth)}
  `,
    [isSidebarCollapsed, depth]
  );

  return (
    <div className={wrapperClasses}>
      {/* Main menu item container */}
      <div className={paddingClasses}>
        {hasChildren ? (
          <div
            className={containerClasses}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleClick();
              }
            }}
          >
            {/* Menu item icon */}
            {item.icon && (
              <span className="flex items-center z-10">
                <MenuIcon
                  className="!text-white/80"
                  icon={item.icon}
                  isActive={isActive}
                  hasActiveChild={hasActiveChild}
                />
              </span>
            )}

            {/* Menu item label with active state styling */}
            <span className={labelClasses}>
              <div className="relative z-10">{item.label}</div>
              {/* Active state background pill */}
              {isActive && (
                <div className="menu-pill absolute top-0 left-0 w-full h-full z-0 rounded-tl-2xl rounded-bl-2xl bg-slate-50" />
              )}
            </span>

            {/* Chevron icon for expandable items */}
            {isOpen ? <FiChevronDown className="ml-auto" /> : <FiChevronRight className="ml-auto" />}
          </div>
        ) : (
          <Link className={containerClasses} to={String(item.key)}>
            {/* Menu item icon */}
            {item.icon && (
              <span className="flex items-center z-10">
                <MenuIcon
                  className="!text-white/80"
                  icon={item.icon}
                  isActive={isActive}
                  hasActiveChild={hasActiveChild}
                />
              </span>
            )}

            {/* Menu item label with active state styling */}
            <span className={labelClasses}>
              <div className="relative z-10">{item.label}</div>
              {/* Active state background pill */}
              {isActive && (
                <div className="menu-pill absolute top-0 left-0 w-full h-full z-0 rounded-tl-2xl rounded-bl-2xl bg-slate-50" />
              )}
            </span>
          </Link>
        )}
      </div>

      {/* Submenu container with animation */}
      {hasChildren && !isSidebarCollapsed && (
        <div
          className={`transition-all duration-200 ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}
        >
          <div className="relative flex justify-center">
            <div className={`absolute top-0 bottom-0 ${getLeftClass(depth)} w-px bg-white/70 z-0`} />
            <Suspense fallback={<div></div>}>
              <RecursiveMenu items={item.children || []} depth={depth + 1} />
            </Suspense>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoized export with display name
ExpandedMenuItemComponent.displayName = 'ExpandedMenuItem';
export const ExpandedMenuItem = memo(ExpandedMenuItemComponent);
